package br.com.texeratx.boardgametimeapp.ui.screens.games.edit

import br.com.texeratx.boardgametimeapp.core.i18n.LanguageManager
import br.com.texeratx.boardgametimeapp.data.model.game.UpdateGameRequest
import br.com.texeratx.boardgametimeapp.domain.usecases.games.GetGameCategoriesUseCase
import br.com.texeratx.boardgametimeapp.domain.usecases.games.GetGameDetailUseCase
import br.com.texeratx.boardgametimeapp.domain.usecases.games.GetGameModesUseCase
import br.com.texeratx.boardgametimeapp.domain.usecases.games.UpdateGameUseCase
import br.com.texeratx.boardgametimeapp.domain.usecases.player.GetPlayersNameUseCase
import br.com.texeratx.boardgametimeapp.ui.screens.games.model.GameCategoryUI
import br.com.texeratx.boardgametimeapp.ui.screens.games.model.GameModeUI
import br.com.texeratx.boardgametimeapp.ui.screens.games.model.PlayerOwnerUI
import cafe.adriel.voyager.core.model.ScreenModel
import cafe.adriel.voyager.core.model.screenModelScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.launch

class EditGameViewModel(
    private val getGameDetail: GetGameDetailUseCase,
    private val getModes: GetGameModesUseCase,
    private val getCategories: GetGameCategoriesUseCase,
    private val getPlayersName: GetPlayersNameUseCase,
    private val updateGame: UpdateGameUseCase,
    private val lang: LanguageManager
) : ScreenModel {

    val state = MutableStateFlow(EditGameState())
    val modes = MutableStateFlow<List<GameModeUI>>(emptyList())
    val isLoading = MutableStateFlow(false)

    fun updateState(reducer: (EditGameState) -> EditGameState) {
        state.value = reducer(state.value)
    }

    fun load(gameId: Long) {
        screenModelScope.launch {
            isLoading.value = true
            state.value = state.value.copy(isLoadingOwners = true)

            getModes().onSuccess { list ->
                modes.value = list.map { GameModeUI(it.id, it.name) }
            }
            getCategories().onSuccess { list ->
                state.value = state.value.copy(
                    categories = list.map { GameCategoryUI(id = it.id, name = it.name) }
                )
            }
            getPlayersName()
                .onSuccess { list ->
                    state.value = state.value.copy(owners = list, isLoadingOwners = false)
                }
                .onFailure {
                    state.value = state.value.copy(
                        isLoadingOwners = false,
                        error = it.message ?: lang.strings.value.errorLoadingOwners
                    )
                }

            getGameDetail(gameId)
                .onSuccess { game ->
                    state.value = state.value.copy(
                        name = game.name,
                        imageUrl = game.imageUrl.orEmpty(),
                        minPlayers = game.minPlayers?.toString().orEmpty(),
                        maxPlayers = game.maxPlayers?.toString().orEmpty(),
                        duration = game.duration?.toString().orEmpty(),
                        bggUrl = game.bggUrl.orEmpty(),
                        manualUrl = game.manualUrl.orEmpty(),
                        spotifyUrl = game.spotifyUrl.orEmpty(),
                        modeId = game.modeId,
                        categoryId = game.categoryId,
                        selectedOwners = game.owners.map { owner ->
                            PlayerOwnerUI(id = owner.id, name = owner.name)
                        },
                        error = null
                    )
                }
                .onFailure {
                    state.value = state.value.copy(error = it.message ?: lang.strings.value.errorLoadingGames)
                }

            isLoading.value = false
        }
    }

    fun save(gameId: Long, onSuccess: () -> Unit) {
        screenModelScope.launch {
            val s = state.value

            if (s.modeId == null) {
                updateState { it.copy(error = lang.strings.value.categoryAndModeRequired) }
                return@launch
            }
            if (s.selectedOwners.isEmpty()) {
                updateState { it.copy(error = lang.strings.value.selectAtLeastOneOwner) }
                return@launch
            }

            updateState { it.copy(isSaving = true, error = null) }

            val result = updateGame(
                gameId,
                UpdateGameRequest(
                    gameModeId = s.modeId,
                    gameCategoryId = s.categoryId,
                    spotifyUrl = s.spotifyUrl.ifBlank { null },
                    manualUrl = s.manualUrl.ifBlank { null },
                    status = 1,
                    ownerIds = s.selectedOwners.map { it.id }
                )
            )

            result.fold(
                onSuccess = {
                    updateState { it.copy(isSaving = false) }
                    onSuccess()
                },
                onFailure = { err ->
                    updateState {
                        it.copy(
                            isSaving = false,
                            error = err.message ?: lang.strings.value.errorSavingGame
                        )
                    }
                }
            )
        }
    }

    fun onCategoryChange(categoryId: Long?) {
        updateState { it.copy(categoryId = categoryId) }
    }

    fun addOwner(owner: PlayerOwnerUI) {
        updateState { current ->
            if (current.selectedOwners.any { it.id == owner.id }) current
            else current.copy(selectedOwners = current.selectedOwners + owner)
        }
    }

    fun removeOwner(ownerId: Long) {
        updateState { current ->
            current.copy(selectedOwners = current.selectedOwners.filterNot { it.id == ownerId })
        }
    }
}
